import { default as React, useState, useEffect } from 'react';
import { makeStyles, Grid } from '@material-ui/core';
import { useDataSourceContext } from '../../contexts/DataSourceContext';
import { useLoading } from '../../contexts/LoadingContext';
import { useGrafanaQuery } from '../../hooks/useGrafanaQuery';
import { HeaderToolbar } from '../ObservabilityPage/components/HeaderToolbar';
import { NoDataSourceMessage } from '../ObservabilityPage/components/NoDataSourceMessage';
import { ErrorDisplay } from '../ObservabilityPage/components/ErrorDisplay';
import { TimeSeriesChart } from '../Visualization/TimeSeriesChart';
import { LogsViewer } from '../Visualization/LogsViewer';
import { useBackstageQueryBuilder } from '../ObservabilityPage/hooks/useBackstageQueryBuilder';
import { BackstageTopErrors } from './BackstageTopErrors';
import { BackstageLogsSkeleton } from './BackstageLogsSkeleton';

const useStyles = makeStyles(theme => ({
  contentRoot: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  chartAndErrorsRow: {
    marginBottom: theme.spacing(3),
  },
  logsSection: {
    marginTop: theme.spacing(3),
  },
}));

export interface BackstageLogsProps {
  /** Se true, esconde o HeaderToolbar (útil quando embarcado em outra página que já tem o header) */
  hideHeader?: boolean;
  /** Time range externo - se fornecido, o componente não gerencia seu próprio time range */
  externalTimeRange?: string;
  /** Callback quando o time range muda */
  onTimeRangeChange?: (range: string) => void;
}

/**
 * Componente reutilizável que exibe logs do Backstage.
 * Pode ser usado na aba "Backstage" da Observabilidade ou embarcado em outras partes do Backstage.
 */
export const BackstageLogs: React.FC<BackstageLogsProps> = ({
  hideHeader = false,
  externalTimeRange,
  onTimeRangeChange,
}) => {
  const classes = useStyles();
  const { selectedDataSource, loading: dataSourceLoading } = useDataSourceContext();
  const { showLoading, hideLoading } = useLoading();
  const [hasQueryErrors, setHasQueryErrors] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [internalTimeRange, setInternalTimeRange] = useState('1h');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const timeRange = externalTimeRange ?? internalTimeRange;

  const changeTimeRange = (range: string) => {
    if (onTimeRangeChange) {
      onTimeRangeChange(range);
    } else {
      setInternalTimeRange(range);
    }
  };

  // Construir as queries específicas do backstage
  const queries = useBackstageQueryBuilder(selectedDataSource, timeRange);

  // Buscar todos os logs do backstage (para o gráfico)
  const {
    data: allLogsData,
    loading: allLogsLoading,
    refresh: refreshAllLogs,
    error: allLogsError,
    hasMore: allLogsHasMore,
    loadMore: loadMoreAllLogs,
  } = useGrafanaQuery(selectedDataSource ? queries.backstageAllLogsQuery : null);

  // Buscar logs de erro do backstage (para o top errors)
  const {
    data: topErrorLogsData,
    loading: topErrorLogsLoading,
    refresh: refreshTopErrorLogs,
    error: topErrorLogsError,
  } = useGrafanaQuery(selectedDataSource ? queries.backstageTopErrorLogsQuery : null);

  // Verificar erros nas queries
  useEffect(() => {
    const errors = [allLogsError, topErrorLogsError];
    setHasQueryErrors(errors.some(error => error !== null));
  }, [allLogsError, topErrorLogsError]);

  // Atualizar todas as métricas
  const refreshAllMetrics = () => {
    if (selectedDataSource) {
      setIsRefreshing(true);
      refreshAllLogs();
      refreshTopErrorLogs();
      setLastRefreshed(new Date());
    }
  };

  // Gerenciar o estado de loading
  useEffect(() => {
    const dataLoading = allLogsLoading || topErrorLogsLoading;
    setIsLoading(dataLoading);

    if (!dataLoading) {
      setIsRefreshing(false);
    }

    if (dataLoading) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [allLogsLoading, topErrorLogsLoading, showLoading, hideLoading]);

  // Executar queries quando a fonte de dados ou time range mudar.
  // `refreshAllMetrics` é função local que captura outros estados — incluí-la
  // na deps re-dispararia o efeito a cada render.
  useEffect(() => {
    if (selectedDataSource) {
      refreshAllMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataSource, timeRange]);

  // Mostrar skeleton enquanto as fontes de dados estão sendo carregadas ou durante o carregamento inicial
  if (dataSourceLoading || (isLoading && !allLogsData) || isRefreshing) {
    return <BackstageLogsSkeleton hideHeader={hideHeader} />;
  }

  // Mostrar mensagem apenas quando o loading terminou e realmente não há fonte de dados
  if (!selectedDataSource) {
    return <NoDataSourceMessage timeRange={timeRange} changeTimeRange={changeTimeRange} />;
  }

  return (
    <div className={classes.contentRoot}>
      {/* Header com seletor de período */}
      {!hideHeader && (
        <HeaderToolbar timeRange={timeRange} changeTimeRange={changeTimeRange} />
      )}

      {/* Aviso de erro */}
      {hasQueryErrors && (
        <ErrorDisplay
          error={allLogsError || topErrorLogsError || new Error('Algumas métricas do Backstage não puderam ser carregadas.')}
        />
      )}

      {/* Gráfico de logs + Top 5 erros */}
      <Grid container spacing={3} className={classes.chartAndErrorsRow}>
        <Grid item xs={12} md={8}>
          <TimeSeriesChart
            data={allLogsData || { results: {} }}
            title="Logs do Backstage por Tipo"
            onRefresh={refreshAllLogs}
            lastRefreshed={lastRefreshed}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <BackstageTopErrors
            data={topErrorLogsData}
            onRefresh={refreshTopErrorLogs}
            lastRefreshed={lastRefreshed}
            loading={topErrorLogsLoading}
          />
        </Grid>
      </Grid>

      {/* Lista de logs com filtros */}
      <div className={classes.logsSection}>
        <LogsViewer
          data={allLogsData || { results: {} }}
          title="Logs do Backstage"
          onRefresh={refreshAllLogs}
          onLoadMore={(offset, limit) => loadMoreAllLogs(offset, limit)}
          lastRefreshed={lastRefreshed}
          loading={allLogsLoading}
          hasMore={allLogsHasMore}
        />
      </div>
    </div>
  );
};